import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { buildPublicMediaUrl } from "../AttachmentPreview";

const BACKGROUNDS = ["#075e54", "#0f766e", "#134e4a", "#1e3a5f", "#3f2e6d"];
const SLIDE_MS = 5000;

const useStyles = makeStyles(theme => ({
  wrap: {
    width: "100%",
    maxWidth: 280,
    margin: "0 auto",
  },
  label: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.4,
    color: theme.palette.text.secondary,
    marginBottom: 10,
    textTransform: "uppercase",
  },
  phone: {
    position: "relative",
    width: "100%",
    aspectRatio: "9 / 16",
    maxHeight: 460,
    borderRadius: 22,
    overflow: "hidden",
    background: "#111",
    color: "#fff",
    boxShadow: "0 16px 40px rgba(15, 23, 42, .28)",
  },
  media: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    background: "#000",
  },
  shade: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(180deg, rgba(0,0,0,.45) 0%, rgba(0,0,0,0) 28%, rgba(0,0,0,0) 62%, rgba(0,0,0,.55) 100%)",
  },
  top: {
    position: "relative",
    zIndex: 2,
    padding: "10px 10px 0",
  },
  bars: {
    display: "flex",
    gap: 4,
  },
  bar: {
    flex: 1,
    height: 3,
    borderRadius: 99,
    background: "rgba(255,255,255,.35)",
    overflow: "hidden",
  },
  fill: {
    height: "100%",
    background: "#fff",
  },
  who: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 10,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    fontWeight: 800,
    background: "#25d366",
    color: "#073b2a",
    boxShadow: "0 0 0 2px #25d366",
  },
  name: {
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1.1,
  },
  when: {
    fontSize: 11,
    opacity: 0.8,
  },
  textStage: {
    position: "absolute",
    inset: 72,
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: 8,
  },
  storyText: {
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1.25,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  caption: {
    position: "absolute",
    left: 16,
    right: 16,
    bottom: 22,
    zIndex: 2,
    fontSize: 15,
    fontWeight: 600,
    lineHeight: 1.3,
    textAlign: "center",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    textShadow: "0 1px 2px rgba(0,0,0,.6)",
  },
  empty: {
    position: "absolute",
    inset: 0,
    display: "grid",
    placeItems: "center",
    padding: 24,
    textAlign: "center",
    color: "rgba(255,255,255,.8)",
    fontSize: 14,
  },
  hit: {
    position: "absolute",
    top: 64,
    bottom: 0,
    width: "50%",
    zIndex: 3,
    border: 0,
    background: "transparent",
    cursor: "pointer",
  },
  hint: {
    marginTop: 8,
    fontSize: 11,
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
  },
}));

const previewVariables = (text) => {
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Buenos días" : hour < 18 ? "Buenas tardes" : "Buenas noches";
  const clock = new Date().toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit" });
  return String(text || "")
    .replace(/\{\{\s*(ms|greeting|gretting)\s*\}\}/gi, greeting)
    .replace(/\{\{\s*hora\s*\}\}/gi, clock);
};

const renderInline = (text, keyPrefix) => {
  const pattern = /```([\s\S]+?)```|\*([^*\n]+)\*|_([^_\n]+)_|~([^~\n]+)~|(\{\{[^}]+\}\})/g;
  const nodes = [];
  let last = 0;
  let match = pattern.exec(text);
  let index = 0;
  while (match) {
    if (match.index > last) nodes.push(text.slice(last, match.index));
    const key = `${keyPrefix}-${index}`;
    if (match[1] != null) nodes.push(<span key={key} style={{ fontFamily: "monospace" }}>{match[1]}</span>);
    else if (match[2] != null) nodes.push(<strong key={key}>{match[2]}</strong>);
    else if (match[3] != null) nodes.push(<em key={key}>{match[3]}</em>);
    else if (match[4] != null) nodes.push(<span key={key} style={{ textDecoration: "line-through" }}>{match[4]}</span>);
    else nodes.push(<span key={key} style={{ background: "rgba(255,255,255,.2)", borderRadius: 4, padding: "0 3px" }}>{match[5]}</span>);
    last = match.index + match[0].length;
    index += 1;
    match = pattern.exec(text);
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
};

const renderStoryText = (value) => {
  const text = previewVariables(value);
  return text.split("\n").map((line, lineIndex) => {
    const quoted = line.startsWith("> ");
    const content = quoted ? line.slice(2) : line;
    return (
      <React.Fragment key={`line-${lineIndex}`}>
        {lineIndex > 0 ? <br /> : null}
        {quoted ? (
          <span style={{ borderLeft: "3px solid rgba(255,255,255,.7)", paddingLeft: 8 }}>
            {renderInline(content, `q-${lineIndex}`)}
          </span>
        ) : renderInline(content, `l-${lineIndex}`)}
      </React.Fragment>
    );
  });
};

const mediaKind = (attachment, mediaPath, mediaName) => {
  const label = attachment?.name || mediaName || mediaPath || "";
  const mime = attachment?.type || "";
  if (mime.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp)$/i.test(label)) return "image";
  if (mime.startsWith("video/") || /\.(mp4|webm|mov|3gp)$/i.test(label)) return "video";
  if (mime.startsWith("audio/") || /\.(mp3|ogg|m4a|wav|opus)$/i.test(label)) return "audio";
  return label ? "file" : "";
};

export default function StatusStoryPreview({
  connectionName,
  channel = "whatsapp",
  messages = [],
  attachment,
  mediaPath,
  mediaName,
}) {
  const classes = useStyles();
  const [blobUrl, setBlobUrl] = useState("");
  const [index, setIndex] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const texts = useMemo(
    () => (messages || []).map(item => String(item || "").trim()).filter(Boolean),
    [messages]
  );
  const kind = mediaKind(attachment, mediaPath, mediaName);
  const slides = useMemo(() => {
    if (!texts.length && !kind) return [];
    if (!texts.length) return [{ text: "", withMedia: true }];
    return texts.map((text, slideIndex) => ({ text, withMedia: slideIndex === 0 && Boolean(kind) }));
  }, [texts, kind]);

  useEffect(() => {
    if (!attachment) {
      setBlobUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(attachment);
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [attachment]);

  useEffect(() => {
    setIndex(0);
    setElapsed(0);
  }, [slides.length, blobUrl, mediaPath]);

  useEffect(() => {
    if (slides.length < 1) return undefined;
    const timer = setInterval(() => {
      setElapsed(current => {
        if (current + 100 >= SLIDE_MS) {
          setIndex(position => (position + 1) % slides.length);
          return 0;
        }
        return current + 100;
      });
    }, 100);
    return () => clearInterval(timer);
  }, [slides.length]);

  const previewUrl = blobUrl || buildPublicMediaUrl(mediaPath);
  const slide = slides[index] || null;
  const background = BACKGROUNDS[index % BACKGROUNDS.length];
  const showMedia = Boolean(slide?.withMedia && previewUrl && (kind === "image" || kind === "video"));
  const initial = (connectionName || "E").trim().charAt(0).toUpperCase();
  const hint = channel === "telegram"
    ? "Así se ve la historia en la cuenta. No abre un chat ni envía un mensaje directo."
    : "Así se ve en Mi estado. No abre un chat ni envía un mensaje directo.";

  const go = direction => {
    if (!slides.length) return;
    setIndex(position => (position + direction + slides.length) % slides.length);
    setElapsed(0);
  };

  return (
    <Box className={classes.wrap}>
      <Typography className={classes.label}>Vista previa del estado</Typography>
      <Box className={classes.phone} style={{ background: showMedia ? "#000" : background }} aria-label="Vista previa del estado">
        {showMedia && kind === "image" && <img className={classes.media} src={previewUrl} alt="" />}
        {showMedia && kind === "video" && <video className={classes.media} src={previewUrl} autoPlay muted loop playsInline />}
        <Box className={classes.shade} />
        <Box className={classes.top}>
          <Box className={classes.bars}>
            {(slides.length ? slides : [{}]).map((_, barIndex) => (
              <Box key={barIndex} className={classes.bar}>
                <Box
                  className={classes.fill}
                  style={{ width: barIndex < index ? "100%" : barIndex === index ? `${(elapsed / SLIDE_MS) * 100}%` : "0%" }}
                />
              </Box>
            ))}
          </Box>
          <Box className={classes.who}>
            <Box className={classes.avatar}>{initial}</Box>
            <Box>
              <Typography className={classes.name}>{connectionName || "Tu cuenta"}</Typography>
              <Typography className={classes.when}>ahora</Typography>
            </Box>
          </Box>
        </Box>
        {!slide && <Box className={classes.empty}>Escribe un texto o elige una foto o un video para ver el estado.</Box>}
        {slide && !showMedia && kind !== "audio" && kind !== "file" && (
          <Box className={classes.textStage}>
            <Typography className={classes.storyText}>{slide.text ? renderStoryText(slide.text) : "Tu estado"}</Typography>
          </Box>
        )}
        {slide?.withMedia && (kind === "audio" || kind === "file") && (
          <Box className={classes.textStage}>
            <Typography className={classes.storyText}>{slide.text ? renderStoryText(slide.text) : (kind === "audio" ? "Nota de voz" : (mediaName || attachment?.name || "Archivo"))}</Typography>
          </Box>
        )}
        {showMedia && slide?.text && <Typography className={classes.caption}>{renderStoryText(slide.text)}</Typography>}
        <button type="button" className={classes.hit} style={{ left: 0 }} aria-label="Estado anterior" onClick={() => go(-1)} />
        <button type="button" className={classes.hit} style={{ right: 0 }} aria-label="Estado siguiente" onClick={() => go(1)} />
      </Box>
      <Typography className={classes.hint}>{hint}</Typography>
    </Box>
  );
}
